/**
 * CusaToastNotification
 *
 * Usage:
 *
 * 1. Create a single toast:
 *    CusaToastNotification.create('Hello world!', 'success', 4000);
 *
 * 2. Manual test single toast (shortcut):
 *    CusaToastNotification.test();
 *
 * 3. Create multiple queued toasts:
 *    CusaToastNotification.testQueue();
 *
 * 4. Customize default duration and queue spacing (not persistant on page reload):
 *    CusaToastNotification.setDefaults({ duration: 5000, spacing: 2000 });
 *
 *
 * Requirements:
 * - Insert a container element with id="guardian-toast-container" into the page.
 *   -- There should be one in `theme.liquid` already but it's worth checking.
 * - Supported toast types: "info", "success", "warning", "error".
 * - A maximum of 3 toasts can be visible at once; others are queued automatically.
 *
 * Special Notes:
 * - Duration can be a number (milliseconds) or the string "infinite".
 *   - Using "infinite" will keep the toast on screen until manually dismissed.
 */

const COLORS = {
	info: '#2e86ab',
	success: '#28a745',
	warning: '#ffc107',
	error: '#dc3545'
};

const TOAST_ICONS = {
	info: `<svg viewBox="0 0 24 24" class="guardian-toast-icon-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="${COLORS.info}"/><text x="12" y="13" font-size="12" fill="#fff" text-anchor="middle" font-family="Arial" dy=".3em">i</text></svg>`,
	success: `<svg viewBox="0 0 24 24" class="guardian-toast-icon-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="20" height="20" rx="4" ry="4" fill="${COLORS.success}"/><polyline points="8 12 11 15 16 9" fill="none" stroke="#fff" stroke-width="2"/></svg>`,
	warning: `<svg viewBox="0 0 24 24" class="guardian-toast-icon-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M1 21h22L12 2 1 21z" fill="${COLORS.warning}" stroke="${COLORS.warning}" stroke-linejoin="round" stroke-linecap="round" stroke-width="1.5" /><line x1="12" y1="8" x2="12" y2="13" stroke="#000" stroke-width="2"/><circle cx="12" cy="17" r="1" fill="#000"/></svg>`,
	error: `<svg viewBox="0 0 24 24" class="guardian-toast-icon-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="${COLORS.error}"/><line x1="8" y1="8" x2="16" y2="16" stroke="#fff" stroke-width="2"/><line x1="16" y1="8" x2="8" y2="16" stroke="#fff" stroke-width="2"/></svg>`
};

const FADE_DURATION = 300;
class CusaToastNotification extends HTMLElement {
	static queue = [];
	static defaultDuration = 6000;
	static queueSpacing = 1500;
	static durations = new Map(); // toast => duration string

	/**
	 * Sets default values for toast duration and queue spacing.
	 *
	 * Note: Changes made with this method apply only for the current page session and are not persistent after a reload.
	 *
	 * Example:
	 * ```javascript
	 * // Set default toast duration to 5 seconds and queue spacing to 2 seconds
	 * CusaToastNotification.setDefaults({ duration: 5000, spacing: 2000 });
	 * ```
	 *
	 * Notes:
	 * - This only affects the current page session and will reset on reload.
	 * - You can use `duration: 'infinite'` to make a toast stay until manually dismissed.
	 *
	 * @param {Object} options - Settings object.
	 * @param {number} [options.duration] - Default duration for toasts (in milliseconds).
	 * @param {number} [options.spacing] - Default spacing between queued toasts (in milliseconds).
	 */
	static setDefaults(options) {
		const { duration, spacing } = options;
		if (typeof duration === 'number') {
			CusaToastNotification.defaultDuration = duration;
		}
		if (typeof spacing === 'number') {
			CusaToastNotification.queueSpacing = spacing;
		}
	}

	/**
	 * Creates a single test toast notification.
	 *
	 * @param {string} [message='This is a test toast!'] - Message to display.
	 * @param {string} [type='info'] - Type of toast (info, success, warning, error).
	 * @param {number} [duration=3000] - Duration in milliseconds.
	 */
	static test(message = 'This is a test toast!', type = 'info', duration = 3000) {
		CusaToastNotification.create(message, type, duration);
	}

	/**
	 * Creates a sequence of test toasts spaced apart.
	 *
	 * @param {Array<Object>} [toasts] - Array of toast objects with message, type, and duration.
	 * @param {number} [spacing=CusaToastNotification.queueSpacing] - Delay between toasts in milliseconds.
	 */
	static testQueue(
		toasts = [
			{ message: 'First toast', type: 'info', duration: 3000 },
			{ message: 'Second toast', type: 'success', duration: 3500 },
			{ message: 'Third toast', type: 'warning', duration: 4000 },
			{ message: 'Fourth toast', type: 'error', duration: 3000 },
			{ message: 'Fifth toast', type: 'info', duration: 3000 },
			{ message: 'Sixth toast', type: 'success', duration: 3200 }
		],
		spacing = CusaToastNotification.queueSpacing
	) {
		toasts.forEach((t, i) => {
			setTimeout(() => {
				CusaToastNotification.create(t.message, t.type, t.duration, t.title);
			}, i * spacing);
		});
	}

	/**
	 * Retrieves the configured duration for a specific toast.
	 *
	 * @param {HTMLElement} toast - The toast element.
	 * @returns {number|undefined} Duration in milliseconds or undefined if not set.
	 */
	static getToastDuration(toast) {
		return CusaToastNotification.durations.get(toast);
	}

	/**
	 * Initializes a CusaToastNotification instance.
	 */
	constructor() {
		super();
		this.toast = null;
		this.messageEl = null;
		this.dismissBtn = null;
		this.hideTimeout = null;
		this.startX = 0;
		this.titleEl = null;
	}

	/**
	 * Called when the toast is connected to the DOM. Sets up event listeners.
	 */
	connectedCallback() {
		this.toast = this.querySelector('.guardian-toast-notification');
		this.messageEl = this.querySelector('.guardian-toast-msg');
		this.titleEl = this.querySelector('.guardian-toast-title');
		this.dismissBtn = this.querySelector('[data-dismiss-toast]');
		this.dismissBtn?.addEventListener('click', this.hide.bind(this));

		if (this.toast) {
			this.toast.addEventListener(
				'touchstart',
				(e) => {
					this.startX = e.touches[0].clientX;
					this.startY = e.touches[0].clientY;
				},
				{ passive: true }
			);

			this.toast.addEventListener(
				'touchmove',
				(e) => {
					e.preventDefault();
					const deltaX = e.touches[0].clientX - this.startX;
					const deltaY = e.touches[0].clientY - this.startY;
					const delta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
					if (delta > 75) {
						this.hide();
					}
				},
				{ passive: false }
			);

			this.toast.addEventListener('touchend', () => {
				// No transform reset needed
			});
		}
	}

	/**
	 * Called when the toast is disconnected from the DOM. Cleans up event listeners.
	 */
	disconnectedCallback() {
		this.dismissBtn?.removeEventListener('click', this.hide.bind(this));
		// Add cleanup if needed here later — in this case, we're not removing swipe events for simplicity
	}

	/**
	 * Displays the toast with the given message, type, and duration.
	 *
	 * @param {string} [message=''] - The message to display.
	 * @param {string} [type='info'] - The type of toast.
	 * @param {number} [duration] - Duration to show the toast in milliseconds.
	 * @param {string} [title=''] - Optional title for the toast.
	 */
	show(message = '', type = 'info', duration, title = '') {
		if (!this.toast || !this.messageEl) return;
		this.toast.setAttribute('data-type', type);
		this.toast.classList.remove('info', 'success', 'warning', 'error');
		this.toast.classList.add(type);
		const div = document.createElement('div');
		div.innerHTML = message;
		div.querySelectorAll('*').forEach((el) => {
			if (el.tagName !== 'A') {
				el.replaceWith(document.createTextNode(el.textContent));
			} else {
				const cleanLink = document.createElement('a');
				cleanLink.href = el.href;
				cleanLink.textContent = el.textContent;
				cleanLink.target = '_blank';
				cleanLink.rel = 'noopener noreferrer';
				el.replaceWith(cleanLink);
			}
		});
		this.messageEl.innerHTML = '';
		this.messageEl.append(...div.childNodes);
		if (this.titleEl) {
			this.titleEl.textContent = title;
		}
		this.toast.classList.remove('hidden');
		this.toast.classList.add('show', 'guardian-toast-fade');
		clearTimeout(this.hideTimeout);
		const timeout =
			typeof duration === 'number'
				? duration
				: this.dataset.duration === 'infinite'
				? 0
				: CusaToastNotification.defaultDuration;
		if (timeout > 0) {
			this.hideTimeout = setTimeout(() => this.hide(), timeout);
		}
	}

	/**
	 * Hides the toast and handles removal and queued toasts.
	 */
	hide() {
		if (!this.toast) return;
		this.toast.classList.remove('show');
		this.toast.classList.add('fade-out');

		setTimeout(() => {
			this.toast.classList.remove('fade-out');
			this.toast.classList.add('hidden');
			this.remove();

			CusaToastNotification.durations.delete(this);

			// If there's a queued toast, show it next
			if (CusaToastNotification.queue.length > 0) {
				const next = CusaToastNotification.queue.shift();
				setTimeout(() => {
					CusaToastNotification.create(next.message, next.type, next.duration, next.title);
				}, CusaToastNotification.queueSpacing);
			}
		}, FADE_DURATION); // match the CSS animation duration
	}

	/**
	 * Creates and displays a new toast notification in the DOM.
	 *
	 * @param {string} message - Message to display.
	 * @param {string} [type='info'] - Type of toast.
	 * @param {number|string} [duration] - Duration in milliseconds or 'infinite'.
	 * @param {string} [title=''] - Optional title for the toast.
	 */
	static create(message, type = 'info', duration, title = '') {
		const container = document.getElementById('guardian-toast-container');
		if (!container) return;

		// Limit the number of toasts to 3, else queue
		const existingToasts = container.querySelectorAll('cusa-toast-notification');
		if (existingToasts.length >= 3) {
			CusaToastNotification.queue.push({ message, type, duration, title });
			return;
		}

		const toast = document.createElement('cusa-toast-notification');
		if (duration === 'infinite') {
			toast.dataset.duration = 'infinite';
		}
		toast.innerHTML = `
    <div class="guardian-toast-notification ${type} hidden fade-in" role="status" aria-live="polite" data-type="${type}">
			<div class="guardian-toast-inner-wrapper">
				<div class="guardian-toast-icon-wrapper">
					<span class="guardian-toast-icon" aria-hidden="true">
						${TOAST_ICONS[type] || TOAST_ICONS.info}
					</span>
				</div>
				<div class="guardian-toast-body">
					${title ? `<strong class="guardian-toast-title">${title}</strong>` : ''}
					<p class="guardian-toast-msg">${message}</p>
				</div>
				<button type="button" class="guardian-toast-close-btn" data-dismiss-toast aria-label="Dismiss notification">×</button>
			</div>
    </div>
  `;
		toast.style.transition = `transform ${FADE_DURATION}ms ease, opacity ${FADE_DURATION}ms ease`;
		toast.style.marginBottom = '1rem';
		container.appendChild(toast);
		if (duration) {
			CusaToastNotification.durations.set(toast, duration);
		}
		toast.show?.(message, type, duration === 'infinite' ? 0 : duration, title);
		return toast;
	}
	/**
	 * Dismisses a toast by element or ID.
	 *
	 * @param {HTMLElement|string} toastOrId - The toast element or its DOM ID.
	 */
	static dismiss(toastOrId) {
		const el = typeof toastOrId === 'string' ? document.getElementById(toastOrId) : toastOrId;

		if (!el) {
			console.warn('[TOAST] No toast element found for dismissal.');
			return;
		}

		if (typeof el.hide === 'function') {
			el.hide();
		} else {
			el.remove();
		}
	}
	/**
	 * Dismisses all active toasts immediately.
	 */
	static clearAll() {
		document.querySelectorAll('cusa-toast-notification')?.forEach((el) => {
			if (typeof el.hide === 'function') {
				el.hide();
			} else {
				el.remove();
			}
		});
		CusaToastNotification.queue = [];
	}
}

window.CusaToastNotification = CusaToastNotification;

customElements.define('cusa-toast-notification', CusaToastNotification);
